import {
  loadRecaptchaScript
} from "./chunk-ZD2ESESY.mjs";
import {
  loadGeeTestScript,
  renderGeeTest,
  solveGeeTest
} from "./chunk-G4NYY657.mjs";
import {
  getCaptchaProvider,
  setPresolvedGeeTest,
  useCaptchaLocale,
  useCaptchaProvider,
  usePresolvedGeeTest
} from "./chunk-P2B2TNGX.mjs";
import {
  ApiConfig
} from "./chunk-IITKCRXL.mjs";
import {
  __async
} from "./chunk-RGHW4PYM.mjs";

// src/grecaptcha/useRecaptcha.ts
import { useCallback, useEffect, useRef, useState } from "react";
var useRecaptcha = ({
  v2elementId,
  autoRenderV2,
  v4GeeTestElementId,
  onLoaded,
  onError
} = {}) => {
  const v2Token = useRef(null);
  const v4Widget = useRef(null);
  const loadedFor = useRef(null);
  const [v4Failed, setV4Failed] = useState(false);
  const provider = useCaptchaProvider();
  const locale = useCaptchaLocale();
  const v4Solved = usePresolvedGeeTest();
  const loadV3Script = useCallback(() => __async(null, null, function* () {
    try {
      yield loadRecaptchaScript();
      if (onLoaded) {
        onLoaded();
      }
    } catch (error) {
      const err = error;
      const msg = (err == null ? void 0 : err.message) || "[USE-CAPTCHA]: Something went wrong";
      console.error(msg);
      if (onError) {
        onError(err);
      }
    }
  }), []);
  const renderV2Captcha = useCallback(() => __async(null, null, function* () {
    if (v2elementId && typeof window !== "undefined" && (window == null ? void 0 : window.grecaptcha)) {
      window.grecaptcha.ready(() => {
        var _a, _b;
        const el = document.getElementById(v2elementId);
        if (el && !(el == null ? void 0 : el.firstChild)) {
          try {
            if (!((_b = (_a = ApiConfig.config) == null ? void 0 : _a.grecaptcha) == null ? void 0 : _b.v2Key)) {
              throw new Error(
                "No reCAPTCHA v2 key found in config, please follow usage instruction in https://github.com/nadude/webook-frontend/blob/main/packages/api/README.md"
              );
            }
            const firstChild = document.createElement("div");
            el.appendChild(firstChild);
            window.grecaptcha.render(firstChild, {
              sitekey: ApiConfig.config.grecaptcha.v2Key,
              callback(response) {
                v2Token.current = response;
              }
            });
          } catch (error) {
            console.log(error);
          }
        }
      });
    }
  }), [v2elementId]);
  const removeV2Captcha = () => {
    if (!v2elementId) return;
    const el = document.getElementById(v2elementId);
    if (el == null ? void 0 : el.firstChild) {
      window.grecaptcha.reset();
      el.firstChild.remove();
      v2Token.current = "";
    }
  };
  const loadV4GeeTestScript = useCallback(() => __async(null, null, function* () {
    try {
      yield loadGeeTestScript();
      if (onLoaded) {
        onLoaded();
      }
    } catch (error) {
      const err = error;
      const msg = (err == null ? void 0 : err.message) || "[USE-GEETEST]: Something went wrong";
      console.error(msg);
      if (onError) {
        onError(err);
      }
    }
  }), []);
  useEffect(() => {
    const current = getCaptchaProvider();
    if (loadedFor.current === current) return;
    loadedFor.current = current;
    if (current === "geetest") {
      loadV4GeeTestScript();
    } else {
      loadV3Script();
    }
  }, [provider, loadV3Script, loadV4GeeTestScript]);
  useEffect(() => {
    if (autoRenderV2) {
      renderV2Captcha();
    }
  }, [autoRenderV2, v2elementId, renderV2Captcha]);
  useEffect(() => {
    if (provider !== "geetest" || !v4GeeTestElementId) return;
    let disposed = false;
    setV4Failed(false);
    const fail = (error) => {
      console.error((error == null ? void 0 : error.message) || "[USE-GEETEST]: Something went wrong");
      onError == null ? void 0 : onError(error);
      if (!disposed) setV4Failed(true);
    };
    const el = document.getElementById(v4GeeTestElementId);
    if (!el) {
      fail(new Error(`[USE-GEETEST]: No element with id "${v4GeeTestElementId}" to render into`));
      return;
    }
    renderGeeTest(el, { locale, onError }).then((widget) => {
      if (disposed) return widget.destroy();
      v4Widget.current = widget;
    }).catch(fail);
    return () => {
      var _a;
      disposed = true;
      (_a = v4Widget.current) == null ? void 0 : _a.destroy();
      v4Widget.current = null;
      setPresolvedGeeTest(null);
    };
  }, [provider, locale, v4GeeTestElementId]);
  useEffect(() => {
    var _a;
    if (!v4Solved) {
      (_a = v4Widget.current) == null ? void 0 : _a.reset();
    }
  }, [v4Solved]);
  const executeRecaptcha = (countryCode) => __async(null, null, function* () {
    return new Promise((res, rej) => {
      var _a, _b;
      try {
        if (countryCode && isSpammyCountry(countryCode) && !v2Token.current) {
          return rej({ message: "Please complete captcha" });
        }
        const v3Key = (_b = (_a = ApiConfig.config) == null ? void 0 : _a.grecaptcha) == null ? void 0 : _b.v3Key;
        if (!v3Key) {
          return rej({
            message: "No reCAPTCHA v3 key found in config, please follow usage instruction in https://github.com/nadude/webook-frontend/blob/main/packages/api/README.md"
          });
        }
        window.grecaptcha.ready(() => {
          window.grecaptcha.execute(v3Key, { action: "forced-v2" }).then(res);
        });
      } catch (error) {
        rej(error);
      }
    });
  });
  const executeV4GeeTest = ({ locale: locale2 } = {}) => solveGeeTest(locale2);
  const isSpammyCountry = (countryCode) => {
    var _a, _b, _c;
    return (_c = (_b = (_a = ApiConfig.config) == null ? void 0 : _a.grecaptcha) == null ? void 0 : _b.spammyCountryCodes) == null ? void 0 : _c.includes(countryCode);
  };
  const awaitingCaptcha = provider === "geetest" && !!v4GeeTestElementId && !v4Solved && !v4Failed;
  return {
    provider,
    awaitingCaptcha,
    executeRecaptcha,
    renderV2Captcha,
    removeV2Captcha,
    executeV4GeeTest,
    isSpammyCountry
  };
};

export {
  useRecaptcha
};
//# sourceMappingURL=chunk-VFFO7X2Y.mjs.map